import { useState, useEffect, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { loggingService } from '../logging/loggingService';
import { SOCKET_ACTIONS, LOG_STATUSES } from '../logging/LoggingConstants';
import { getConfigValue } from '../utils/env';

interface UseSocketOptions {
    autoConnect?: boolean;
    enabled?: boolean;
}

let socketInstance: Socket | null = null;
let currentNamespace: string = '';

const cleanupSocket = () => {
    if (socketInstance) {
        socketInstance.disconnect();
        socketInstance.removeAllListeners();
        socketInstance = null;
        currentNamespace = '';
    }
};

const ensureConnected = (socket: Socket): Promise<void> => {
    return new Promise((resolve, reject) => {
        let retries = 0;
        const maxRetries = 3;
        const timer = setTimeout(() => reject(new Error('Connection timeout')), 10000);

        const tryConnect = () => {
            if (socket.connected || retries >= maxRetries) {
                clearTimeout(timer);
                return resolve();
            }
            retries++;
            socket.connect();
        };

        socket.once('connect', tryConnect);
        socket.once('connect_error', (err) => {
            clearTimeout(timer);
            reject(err);
        });

        tryConnect();
    });
};

export const useSocket = (options: UseSocketOptions = {}) => {
    const { autoConnect = true, enabled = true } = options;
    const [isConnected, setIsConnected] = useState(socketInstance?.connected ?? false);

    useEffect(() => {
        const handleConnect = () => setIsConnected(true);
        const handleDisconnect = () => setIsConnected(false);

        // Register socket event listeners on mount
        if (socketInstance) {
            socketInstance.on('connect', handleConnect);
            socketInstance.on('disconnect', handleDisconnect);
        }

        // Cleanup socket event listeners on unmount
        return () => {
            if (socketInstance) {
                socketInstance.off('connect', handleConnect);
                socketInstance.off('disconnect', handleDisconnect);
            }
        };
    }, []);

    const connect = useCallback(async (namespace: string = '', query?: Record<string, string>) => {
        if (!enabled) return;

        const url = getConfigValue('REACT_APP_SOCKET_URL');
        if (!url) {
            console.error('Socket URL not configured');
            return;
        }

        const fullNamespace = namespace || '';
        const needsNewConnection = !socketInstance || socketInstance.disconnected || currentNamespace !== fullNamespace;

        if (needsNewConnection) {
            cleanupSocket(); // Cleanup any previous socket instance

            socketInstance = io(`${url}${fullNamespace}`, {
                autoConnect: false,
                transports: ['websocket'],
                query,
            });
            currentNamespace = fullNamespace;

            // Register socket event listeners
            socketInstance.on('connect', () => {
                setIsConnected(true);
                // Log socket connection for all namespaces
                loggingService.log(SOCKET_ACTIONS.SOCKET_CONNECT, LOG_STATUSES.SUCCESS, {
                    namespace: fullNamespace || '/'
                });
            });
            socketInstance.on('disconnect', () => {
                setIsConnected(false);
                // Log socket disconnection for all namespaces
                loggingService.log(SOCKET_ACTIONS.SOCKET_DISCONNECT, LOG_STATUSES.SUCCESS, {
                    namespace: fullNamespace || '/'
                });
            });
            socketInstance.on('connect_error', (error) => {
                // Log socket connection errors for all namespaces
                loggingService.log(SOCKET_ACTIONS.SOCKET_CONNECT_ERROR, LOG_STATUSES.FAILURE, {
                    namespace: fullNamespace || '/',
                    error: error.message || 'Connection error'
                });
            });

            if (autoConnect) {
                await ensureConnected(socketInstance);
            }
        }
    }, [enabled, autoConnect]);

    const disconnect = useCallback(() => {
        cleanupSocket(); // Cleanup the socket instance manually
        setIsConnected(false);
    }, []);

    const emit = useCallback((event: string, data?: any, callback?: (res: any) => void) => {
        if (!socketInstance) {
            console.warn('Socket not initialized');
            return;
        }
        ensureConnected(socketInstance)
            .then(() => socketInstance!.emit(event, data, callback))
            .catch((err) => {
                console.error('Emit failed:', err);
            });
    }, []);

    const on = useCallback(<T = any>(event: string, handler: (data: T) => void) => {
        if (!socketInstance) {
            console.warn('Socket not initialized');
            return;
        }
        socketInstance.on(event, handler);
    }, []);

    const off = useCallback((event: string, handler?: (...args: any[]) => void) => {
        if (!socketInstance) return;
        if (handler) {
            socketInstance.off(event, handler);
        } else {
            socketInstance.removeAllListeners(event);
        }
    }, []);

    return { isConnected, emit, on, off, connect, disconnect };
};
